import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Users } from "lucide-react";
import api from "@/lib/api";

/**
 * Public, no-auth matrix view.
 * URL: /matrix/:token
 * Renders each committee card with its portfolios grid.
 * Green tile = portfolio allotted. Grey tile = still open.
 * No delegate names are ever exposed.
 */
export default function PublicMatrixPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/matrix/${token}`);
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "This matrix link is invalid or has been revoked." : "Failed to load matrix.");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-100 grid place-items-center text-rose-600 mb-3">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Link unavailable</h1>
          <p className="text-slate-500 text-sm mt-2">{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  const { event, committees, total_portfolios, total_allotted } = data;
  const openCount = total_portfolios - total_allotted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/40">
      {/* Top banner */}
      <div className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 20% 30%, rgba(251,146,60,0.3), transparent 60%), radial-gradient(circle at 80% 60%, rgba(16,185,129,0.35), transparent 55%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-10">
          <div className="text-xs font-bold tracking-widest text-orange-300 uppercase mb-2">Country Matrix · Live</div>
          <h1 className="font-display text-3xl md:text-5xl font-black leading-tight" data-testid="matrix-event-name">{event.name}</h1>
          {event.school_name && <p className="text-slate-300 text-sm md:text-base mt-1">Hosted by {event.school_name}{event.event_date ? ` · ${event.event_date}` : ""}</p>}
          <div className="flex flex-wrap gap-3 mt-6">
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/10 px-4 py-2">
              <div className="text-[10px] tracking-widest text-slate-300 uppercase">Total portfolios</div>
              <div className="text-2xl font-black" data-testid="matrix-total">{total_portfolios}</div>
            </div>
            <div className="rounded-xl bg-emerald-500/20 backdrop-blur border border-emerald-400/30 px-4 py-2">
              <div className="text-[10px] tracking-widest text-emerald-200 uppercase">Allotted</div>
              <div className="text-2xl font-black text-emerald-200" data-testid="matrix-allotted">{total_allotted}</div>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur border border-white/10 px-4 py-2">
              <div className="text-[10px] tracking-widest text-slate-300 uppercase">Open</div>
              <div className="text-2xl font-black text-slate-200" data-testid="matrix-open">{openCount}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {committees.length === 0 && (
          <div className="text-center py-20 text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No committees have been configured for this event yet.</p>
          </div>
        )}

        {committees.map((c, ci) => (
          <motion.section
            key={c.committee_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: ci * 0.06 }}
            className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
            data-testid={`matrix-committee-${c.committee_id}`}
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-display font-bold text-xl text-slate-900 truncate">{c.name}</h2>
                {c.agenda && <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{c.agenda}</p>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-400">
                  <span className="text-emerald-600 font-bold">{c.allotted}</span> of {c.total} allotted
                </div>
                <div className="mt-1 w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
                       style={{ width: `${c.total ? Math.round((c.allotted / c.total) * 100) : 0}%` }} />
                </div>
              </div>
            </div>
            <div className="p-5">
              {c.portfolios.length === 0 && <div className="text-slate-400 text-sm text-center py-6">No portfolios defined for this committee.</div>}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {c.portfolios.map((p, pi) => (
                  <div
                    key={pi}
                    data-testid={`matrix-portfolio-${c.committee_id}-${pi}`}
                    data-allotted={p.allotted ? "true" : "false"}
                    className={
                      p.allotted
                        ? "px-3 py-2.5 rounded-lg bg-emerald-500 text-white font-semibold text-sm shadow-sm border border-emerald-600"
                        : "px-3 py-2.5 rounded-lg bg-slate-100 text-slate-500 font-medium text-sm border border-slate-200"
                    }
                  >
                    <span className="line-clamp-2">{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>
        ))}

        <div className="text-center text-xs text-slate-400 pt-6 pb-2">
          Powered by <span className="font-semibold text-slate-500">MUNner&apos;s</span>
        </div>
      </div>
    </div>
  );
}
